<template>
  <Page>
    <div ref="containerRef" class="flex flex-col h-full min-h-0 space-y-1 p-0">
      <BillOfMaterialsSearchForm
        :initial-filters="filters"
        :loading="loading"
        @submit="updateFilters"
        @reset="resetFilters"
      />

      <BillOfMaterialsToolbar
        v-model:multi-select-enabled="multiSelectEnabled"
        :selection="selectedItems"
        :loading="actionLoading"
        :readonly="isReadOnly"
        @add="openAdd"
        @edit="openEdit"
        @clone="openClone"
        @delete="confirmDelete"
        @in-use-by="handleInUseBy"
        @view="openView"
      />

      <!-- Main Table Section with dynamic height -->
      <div 
        class="relative flex min-h-0 flex-col overflow-hidden rounded-lg bg-background-card shadow-card"
        :style="{ height: mainTableHeight }"
      >
        <div v-if="loading && !items.length" class="flex flex-1 items-center justify-center px-6 py-8 text-sm text-text-light">
          Loading bill of materials…
        </div>

        <div v-else-if="error" class="flex flex-1 items-center justify-center px-6 py-8 text-center text-sm text-error">
          {{ error }}
        </div>

        <div v-else-if="!items.length" class="flex flex-1 items-center justify-center px-6 py-12">
          <div class="max-w-md text-center">
            <i class="fa-solid fa-layer-group text-4xl text-text-muted mb-4"></i>
            <h3 class="text-lg font-semibold text-text mb-2">No bill of materials found</h3>
            <p class="text-text-light">
              {{ hasActiveFilters ? 'Try adjusting or clearing your filters.' : 'Add a bill of material to get started.' }}
            </p>
            <button v-if="!hasActiveFilters && !isReadOnly" class="btn-primary mt-4" type="button" @click="openAdd">
              <i class="fa-solid fa-plus mr-2" aria-hidden="true"></i>
              Add Bill of Material
            </button>
          </div>
        </div>

        <div v-else class="flex-1 flex flex-col overflow-hidden">
          <div v-if="isReadOnly && items.length" class="flex items-center gap-2 px-4 py-1.5 bg-background-elevated border-b border-border text-xs text-text-light">
            <i class="fa-solid fa-circle-info text-accent" aria-hidden="true"></i>
            <span>Tip: Double-click a row to view its details.</span>
          </div>
          <BillOfMaterialsTable
            v-model:selection="selectedItems"
            :rows="items"
            :loading="loading"
            :sort-field="sortState.field"
            :sort-order="sortState.order"
            :multi-select-enabled="multiSelectEnabled"
            :readonly="isReadOnly"
            @sort="onSort"
            @selection-change="selectItem"
            @edit="openEdit"
            @clone="openClone"
            @delete="confirmDeleteFromTable"
            @view="openView"
          />
          <Paginator
            :current-page="page.page"
            :page-count="pageCount"
            :page-size="page.pageSize"
            :total-items="page.totalItems ?? items.length"
            item-label="Bill of Materials"
            class="p-0"
            @update:currentPage="handlePageChange"
            @first="() => handlePageChange(1)"
            @prev="() => handlePageChange(Math.max(1, page.page - 1))"
            @next="() => handlePageChange(Math.min(pageCount, page.page + 1))"
            @last="() => handlePageChange(pageCount)"
          />
        </div>
      </div>
    
      <!-- Resizable Separator -->
      <div 
        class="resize-handle"
        :class="{ 'resize-handle-active': isResizing }"
        @mousedown="startResize"
      >
        <div class="resize-handle-bar">
          <i class="fa-solid fa-grip-lines"></i>
        </div>
      </div>

      <!-- Detail Panels with dynamic height -->
      <div 
        class="grid grid-cols-1 gap-1 lg:grid-cols-2 min-h-0 overflow-hidden"
        :style="{ height: detailPanelsHeight }"
      >
        <!-- Sub-BoMs Panel -->
        <div class="flex flex-col flex-1 gap-3 overflow-hidden">
          <p v-if="subBomTreeError" class="text-sm text-error">{{ subBomTreeError }}</p>
          
          <BillOfMaterialSubBomsTreeTablePanel 
            v-if="selectedItem" 
            :nodes="subBomTreeNodes" 
            :loading="subBomTreeLoading" 
          />
          
          <div 
            v-else 
            class="bg-background-card rounded-lg shadow-card p-6 flex flex-col items-center justify-center text-center h-full"
          >
            <i class="fa-solid fa-cubes text-4xl text-text-muted mb-4"></i>
            <h3 class="text-lg font-semibold text-text mb-2">Sub-Bill Of Materials</h3>
            <p class="text-text-light">Select a bill of material to view its hierarchy.</p>
          </div>
        </div>

        <!-- Assigned Assets Panel -->
        <div class="flex flex-col flex-1 gap-3 overflow-hidden">
          <template v-if="selectedItem">
            <p v-if="assetsError" class="text-sm text-error">{{ assetsError }}</p>
            <BillOfMaterialAssignedAssetsTablePanel :rows="assignedAssets" :loading="assetsLoading" readonly />
          </template>
          
          <div 
            v-else 
            class="bg-background-card rounded-lg shadow-card p-6 flex flex-col items-center justify-center text-center h-full"
          >
            <i class="fa-solid fa-sitemap text-4xl text-text-muted mb-4"></i>
            <h3 class="text-lg font-semibold text-text mb-2">Assigned to Assets</h3>
            <p class="text-text-light">Select a bill of material to view assigned assets.</p>
          </div>
        </div>
      </div>
    </div>

    <CloneBillOfMaterialModal
      :visible="cloneVisible"
      :bill-of-material="cloneTarget"
      :loading="actionLoading"
      @close="closeClone"
      @confirm="handleConfirmClone"
    />

    <BillOfMaterialInUseByModal
      v-model:visible="inUseByVisible"
      :selected-item="inUseByTarget"
      :rows="parents"
      :loading="parentsLoading"
      :error="parentsError"
      @update:visible="handleInUseByVisibleChange"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onActivated, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { addToast } from '@/shared/utils/toastMessages';
import Page from '@/shared/components/layouts/Page.vue';
import Paginator from '@/shared/components/ui/Paginator.vue';
import BillOfMaterialsSearchForm from '../../components/bill-of-materials/BillOfMaterialsSearchForm.vue';
import BillOfMaterialsTable from '../../components/bill-of-materials/BillOfMaterialsTable.vue';
import BillOfMaterialsToolbar from '../../components/bill-of-materials/BillOfMaterialsToolbar.vue';
import BillOfMaterialSubBomsTreeTablePanel from '../../components/bill-of-materials/BillOfMaterialSubBomsTreeTablePanel.vue';
import BillOfMaterialAssignedAssetsTablePanel from '../../components/bill-of-materials/BillOfMaterialAssignedAssetsTablePanel.vue';
import CloneBillOfMaterialModal from '../../components/bill-of-materials/CloneBillOfMaterialModal.vue';
import BillOfMaterialInUseByModal from '../../components/bill-of-materials/BillOfMaterialInUseByModal.vue';
import { useBillOfMaterials } from '../../composables/useBillOfMaterials';
import { useUserRole } from '@/shared/composables/useUserRole';
import { isProjectViewerRole } from '@/shared/utils/projectAccess';
import type { BillOfMaterialDto } from '@/shared/services/billOfMaterials';

definePage({
  meta: {
    title: 'Bill of Materials',
  },
});

const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const { userRole } = useUserRole();
const isReadOnly = computed(() => isProjectViewerRole(userRole.value));

const {
  items,
  loading,
  actionLoading,
  error,
  page,
  filters,
  selectedItem,
  selectedItems, // Selected items
  sortState,
  hasActiveFilters,
  subBomTreeNodes,
  subBomTreeLoading,
  subBomTreeError,
  assignedAssets,
  assetsLoading,
  assetsError,
  parents,
  parentsLoading,
  parentsError,
  fetchItems,
  updateFilters,
  resetFilters,
  handlePageChange,
  onSort,
  selectItem,
  resetListState,
  resetParents,
  fetchParents,
  deleteBillOfMaterial,
  bulkDeleteBillOfMaterials, // Bulk delete
  cloneBillOfMaterial,
} = useBillOfMaterials();

const cloneVisible = ref(false);
const cloneTarget = ref<BillOfMaterialDto | null>(null);

const inUseByVisible = ref(false);
const inUseByTarget = ref<BillOfMaterialDto | null>(null);

const multiSelectEnabled = ref(false);

// Resizable separator state
const containerRef = ref<HTMLElement | null>(null);
const isResizing = ref(false);
const mainTableHeightPercent = ref(60); // Default: 60% for main table, 40% for details

// Load saved preference from localStorage
onMounted(() => {
  const saved = localStorage.getItem('bom-table-split');
  if (saved) {
    const parsed = parseInt(saved, 10);
    if (!isNaN(parsed) && parsed >= 20 && parsed <= 80) {
      mainTableHeightPercent.value = parsed;
    }
  }
});

// Computed heights for main table and detail panels
const mainTableHeight = computed(() => `${mainTableHeightPercent.value}%`);
const detailPanelsHeight = computed(() => `${100 - mainTableHeightPercent.value}%`);

// Resize handlers
function startResize(event: MouseEvent): void {
  event.preventDefault();
  isResizing.value = true;
  
  const handleMouseMove = (e: MouseEvent): void => {
    if (!containerRef.value || !isResizing.value) return;
    
    const containerRect = containerRef.value.getBoundingClientRect();
    const containerHeight = containerRect.height;
    const mouseY = e.clientY - containerRect.top;
    
    // Calculate percentage (with min/max constraints)
    let newPercent = (mouseY / containerHeight) * 100;
    newPercent = Math.max(20, Math.min(80, newPercent)); // Constrain between 20% and 80%
    
    mainTableHeightPercent.value = newPercent;
  };
  
  const handleMouseUp = (): void => {
    isResizing.value = false;
    // Save preference to localStorage
    localStorage.setItem('bom-table-split', mainTableHeightPercent.value.toString());
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
  
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
}

const pageCount = computed(() => page.value.totalPages ?? Math.max(1, Math.ceil((page.value.totalItems ?? 0) / page.value.pageSize)));

onMounted(() => {
  resetListState();
  void fetchItems();
});

onActivated(() => {
  resetListState();
  void fetchItems();
});

async function openAdd(): Promise<void> {
  if (isReadOnly.value) return;
  actionLoading.value = true;

  try {
    void router.push({
      name: '/spares/billofmaterials/[billOfMaterialId]',
      params: { billOfMaterialId: 'new' },
    });
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'Failed to create bill of material';
    addToast(toast, { severity: 'error', summary: 'Create failed', detail });
  } finally {
    actionLoading.value = false;
  }
}

function openEdit(item: BillOfMaterialDto): void {
  if (isReadOnly.value) return;
  if (!item.id) return;
  void router.push({
    name: '/spares/billofmaterials/[billOfMaterialId]',
    params: { billOfMaterialId: item.id },
  });
}

function openView(item: BillOfMaterialDto): void {
  if (!item.id) return;
  void router.push({
    name: '/spares/billofmaterials/[billOfMaterialId]',
    params: { billOfMaterialId: item.id },
  });
}

function openClone(item: BillOfMaterialDto): void {
  if (isReadOnly.value) return;
  cloneTarget.value = item;
  cloneVisible.value = true;
}

function closeClone(): void {
  cloneVisible.value = false;
  cloneTarget.value = null;
}

async function handleConfirmClone(newId: string): Promise<void> {
  if (!cloneTarget.value?.id) return;
  try {
    await cloneBillOfMaterial(cloneTarget.value.id, newId);
    addToast(toast, { severity: 'success', summary: 'Cloned', detail: 'Bill of material cloned successfully' });
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'Failed to clone bill of material';
    addToast(toast, { severity: 'error', summary: 'Clone failed', detail });
  } finally {
    closeClone();
  }
}

function confirmDeleteFromTable(item: BillOfMaterialDto): void {
  confirmDelete([item]);
}

function confirmDelete(items: BillOfMaterialDto[]): void {
  if (items.length === 0) return;

  const count = items.length;
  const isSingle = count === 1;
  const firstItem = items[0];
  const label = isSingle
    ? (firstItem?.description ?? firstItem?.billOfMaterialsId ?? 'bill of material')
    : `${count} bill of materials`;

  confirm.require({
    header: isSingle ? 'Delete Bill of Material' : 'Delete Bill of Materials',
    message: isSingle
      ? `Are you sure you want to delete "${label}"?`
      : `Are you sure you want to delete ${label}?`,
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: isSingle ? 'Delete' : `Delete ${count}`,
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async () => {
      if (isSingle && firstItem?.id) {
        try {
          await deleteBillOfMaterial(firstItem.id);
          addToast(toast, { severity: 'success', summary: 'Deleted', detail: 'Bill of material deleted' });
        } catch (err) {
          const detail = err instanceof Error ? err.message : 'Failed to delete bill of material';
          addToast(toast, { severity: 'error', summary: 'Delete failed', detail });
        }
      } else {
        const ids = items.map((m) => m.id).filter((id): id is string => Boolean(id));
        if (ids.length === 0) return;
        
        try {
          const result = await bulkDeleteBillOfMaterials(ids);
          if (result.failed === 0) {
            addToast(toast, {
              severity: 'success',
              summary: 'Deleted',
              detail: `${result.success} bill of material${result.success > 1 ? 's' : ''} deleted successfully`,
            });
          } else {
            addToast(toast, {
              severity: 'warn',
              summary: 'Partial Success',
              detail: `${result.success} deleted, ${result.failed} failed`,
              life: 5000,
            });
          }
        } catch (err) {
          const detail = err instanceof Error ? err.message : 'Failed to bulk delete bill of materials';
          addToast(toast, { severity: 'error', summary: 'Bulk delete failed', detail });
        }
      }
    },
  });
}

function handleInUseBy(item: BillOfMaterialDto): void {
  if (!item.id) return;
  inUseByTarget.value = item;
  inUseByVisible.value = true;
  void fetchParents(item.id);
}

function handleInUseByVisibleChange(value: boolean): void {
  inUseByVisible.value = value;
  if (!value) {
    inUseByTarget.value = null;
    resetParents();
  }
}
</script>

<style scoped lang="postcss">
@reference '../../../../style.css';
.btn-primary {
  @apply inline-flex items-center justify-center rounded-full bg-accent px-4 py-2 text-sm font-semibold text-text-inverse shadow-sm hover:bg-accent-dark transition disabled:cursor-not-allowed disabled:opacity-60;
}

.resize-handle {
  @apply relative flex items-center justify-center cursor-row-resize select-none;
  height: 12px;
  margin: 2px 0;
  z-index: 10;
}

.resize-handle:hover .resize-handle-bar,
.resize-handle-active .resize-handle-bar {
  @apply bg-accent;
  transform: scaleY(1.5);
}

.resize-handle-bar {
  @apply flex items-center justify-center rounded-full transition-all duration-200;
  background-color: rgb(var(--color-border) / 0.5);
  width: 60px;
  height: 4px;
}

.resize-handle-bar i {
  @apply text-xs;
  color: rgb(var(--color-text-light) / 0.8);
  font-size: 10px;
}

.resize-handle:hover .resize-handle-bar i,
.resize-handle-active .resize-handle-bar i {
  @apply text-text-inverse;
}

/* Prevent text selection during resize */
.resize-handle-active,
.resize-handle-active * {
  user-select: none !important;
  -webkit-user-select: none !important;
}
</style>
